/**
 * Display helpers for Baton resource quantities. Bytes use the decimal
 * convention (KB = 1000, GB = 1e9) to match the pricing engine + the
 * calculator element (brief 105/110), so the dashboard's "256 KB" agrees
 * with what the customer was priced on.
 */

export function formatBytes(bytes: string | number | bigint): string {
  let n: number;
  try {
    n = Number(bytes);
  } catch {
    return '—';
  }
  if (!Number.isFinite(n)) return '—';
  if (n < 0) n = 0;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (n >= 1000 && i < units.length - 1) {
    n /= 1000;
    i += 1;
  }
  return `${i === 0 ? n.toFixed(0) : n.toFixed(1)} ${units[i]}`;
}

/** Compact "time remaining" — `expired` once non-positive. */
export function formatDurationShort(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'expired';
  const d = Math.floor(seconds / 86400);
  if (d >= 1) return `${d}d`;
  const h = Math.floor(seconds / 3600);
  if (h >= 1) return `${h}h`;
  const m = Math.floor(seconds / 60);
  if (m >= 1) return `${m}m`;
  return `${Math.floor(seconds)}s`;
}

export type BatonBadgeVariant =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'destructive'
  | 'success';

/**
 * Map a baton lifecycle `deleteState` to a Badge variant. Semantic, not
 * brand (brief 117): active = success (green, healthy); deleted = destructive
 * (red); tombstoned/scheduled = neutral. `active` must NOT be the brand red.
 */
export function statusBadgeVariant(status: string): BatonBadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'scheduled':
      return 'outline';
    case 'tombstoned':
      return 'secondary';
    case 'deleted':
      return 'destructive';
    default:
      return 'outline';
  }
}

/** Human label for a `WarningEntry.code` (brief 115). */
export function warningLabel(code: string): string {
  switch (code) {
    case 'egress_below_10_percent':
      return 'Low egress remaining';
    case 'writes_below_10_percent':
      return 'Low writes remaining';
    case 'size_below_10_percent':
      return 'Low size remaining';
    case 'time_below_72_hours':
      return 'Expiring soon';
    case 'account_balance_negative':
      return 'Account balance negative';
    case 'token_exhausted':
      return 'Token exhausted';
    default:
      return code.replace(/_/g, ' ');
  }
}

/** Human label for an event-log `event_type` (e.g. `token_issued` → `Token issued`). */
export function formatEventType(eventType: string): string {
  const s = eventType.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}
